'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TurnstileContext } from '@/contexts/TurnstileContext';

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const isProduction = process.env.NODE_ENV === 'production';

export default function TurnstileProvider({ children }) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [token, setToken] = useState(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef(null);
  const waitersRef = useRef([]);
  const scriptRequestedRef = useRef(false);

  // Зареждаме Turnstile само в production. На localhost Cloudflare често връща 400
  // и виджетът ретрайва постоянно – води до множество грешки в конзолата/Network.
  // Виж: https://community.cloudflare.com/t/400-bad-request-turnstile-challenge-error/496344
  const shouldLoadTurnstile = Boolean(siteKey && isProduction);

  /**
   * Зарежда Turnstile скрипта при първа реална нужда — а не на всяка страница.
   *
   * Досега провайдърът инжектираше скрипта в root layout-а с `async=false`, тоест
   * 27 KB блокиращ скрипт на всяка страница, макар че токен се иска само в
   * checkout / login / register / forgot-password / report-issue. Скриптът сам по
   * себе си не прави нищо — токенът идва от виджета — така че зареждането му може
   * спокойно да изчака компонента, който ще го ползва.
   *
   * Извиква се от `TurnstileWidget` при mount и от `waitForToken()` (за всеки
   * случай, ако някой консуматор чака токен без да е рендерирал виджет).
   * Идемпотентно: скриптът се иска най-много веднъж за живота на страницата.
   */
  const ensureScript = useCallback(() => {
    if (!shouldLoadTurnstile || typeof document === 'undefined' || scriptRequestedRef.current) {
      return;
    }
    scriptRequestedRef.current = true;

    if (typeof window !== 'undefined' && window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    const handleLoad = () => setScriptLoaded(true);
    const handleError = () => setScriptLoaded(false);

    // Скриптът може вече да е в DOM-а (напр. втори провайдър или предишна навигация).
    const existingScript = document.querySelector('script[src*="turnstile/v0/api.js"]');
    if (existingScript) {
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    // Скриптът НЕ се маха при unmount: веднъж зареден, `window.turnstile` обслужва
    // всички следващи виджети без повторно теглене.
    document.head.appendChild(script);
  }, [shouldLoadTurnstile]);

  useEffect(() => {
    tokenRef.current = token;
    if (token && waitersRef.current.length > 0) {
      const waiters = waitersRef.current;
      waitersRef.current = [];
      waiters.forEach(({ resolve, timeoutId }) => {
        clearTimeout(timeoutId);
        resolve(token);
      });
    }
  }, [token]);

  const resetTurnstile = useCallback(() => {
    if (typeof window !== 'undefined' && window.turnstile && widgetIdRef.current != null) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setToken(null);
      } catch {
        // ignore
      }
    } else {
      setToken(null);
    }
  }, []);

  const bindWidget = useCallback((widgetId) => {
    widgetIdRef.current = widgetId;
  }, []);

  const clearWidget = useCallback((widgetId = null) => {
    if (widgetId == null || widgetIdRef.current === widgetId) {
      widgetIdRef.current = null;
      setToken(null);
    }
  }, []);

  const waitForToken = useCallback(
    (timeoutMs = 8000) => {
      if (!shouldLoadTurnstile) {
        return Promise.resolve(null);
      }
      if (tokenRef.current) {
        return Promise.resolve(tokenRef.current);
      }
      ensureScript();
      return new Promise((resolve, reject) => {
        const waiter = { resolve, timeoutId: null };
        waiter.timeoutId = setTimeout(() => {
          const idx = waitersRef.current.indexOf(waiter);
          if (idx >= 0) {
            waitersRef.current.splice(idx, 1);
          }
          reject(new Error('turnstile-timeout'));
        }, timeoutMs);
        waitersRef.current.push(waiter);
      });
    },
    [shouldLoadTurnstile, ensureScript],
  );

  const value = useMemo(
    () => ({
      turnstileToken: token,
      getToken: () => token,
      resetTurnstile,
      setTurnstileToken: setToken,
      bindWidget,
      clearWidget,
      waitForToken,
      ensureScript,
      siteKey,
      scriptLoaded,
      shouldLoadTurnstile,
      isTurnstileEnabled: shouldLoadTurnstile,
      isWidgetReady: !shouldLoadTurnstile || scriptLoaded,
      hasToken: Boolean(token),
    }),
    [
      token,
      resetTurnstile,
      bindWidget,
      clearWidget,
      waitForToken,
      ensureScript,
      siteKey,
      scriptLoaded,
      shouldLoadTurnstile,
    ],
  );

  // Без siteKey не предлагаме Turnstile; без shouldLoadTurnstile (dev) не зареждаме скрипт/виджет
  if (!siteKey) {
    return children;
  }

  return (
    <TurnstileContext.Provider value={value}>
      {shouldLoadTurnstile && <link rel="preconnect" href="https://challenges.cloudflare.com" />}
      {children}
    </TurnstileContext.Provider>
  );
}
